// Labelled form controls matching <Input>: every control has a visible
// <label> tied to it, a hint or an error under it, and the shared focus
// ring. Nothing here is decorative-only.
import * as React from "react";
import { cn } from "@/lib/cn";

const CONTROL =
  "w-full rounded-md border border-line-1 bg-bg-2 px-3 text-sm text-t-1 transition-colors focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15 disabled:opacity-60";

function Label({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="text-2xs font-semibold uppercase tracking-wide text-t-3">
      {children}
    </label>
  );
}

function Help({ error, hint, id }: { error?: string | null; hint?: React.ReactNode; id: string }) {
  if (!error && !hint) return null;
  return (
    <p id={id} className={cn("text-2xs", error ? "text-no" : "text-t-3")}>
      {error ?? hint}
    </p>
  );
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: React.ReactNode;
  error?: string | null;
  options: readonly { value: string; label: string }[];
}

export function Select({ label, hint, error, options, className, id, ...rest }: SelectProps) {
  const autoId = React.useId();
  const selectId = id ?? autoId;
  const helpId = `${selectId}-help`;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={selectId}>{label}</Label>
      <select
        id={selectId}
        className={cn(CONTROL, "h-10", error && "border-no/60", className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? helpId : undefined}
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Help error={error} hint={hint} id={helpId} />
    </div>
  );
}

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: React.ReactNode;
  error?: string | null;
}

export function Textarea({ label, hint, error, className, id, ...rest }: TextareaProps) {
  const autoId = React.useId();
  const areaId = id ?? autoId;
  const helpId = `${areaId}-help`;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={areaId}>{label}</Label>
      <textarea
        id={areaId}
        className={cn(CONTROL, "min-h-[76px] py-2", error && "border-no/60", className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? helpId : undefined}
        {...rest}
      />
      <Help error={error} hint={hint} id={helpId} />
    </div>
  );
}

/** A segmented single choice (radio semantics), e.g. scope or role. */
export function Segmented<V extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: V;
  options: readonly { value: V; label: string }[];
  onChange: (v: V) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-2xs font-semibold uppercase tracking-wide text-t-3" aria-hidden>
        {label}
      </span>
      <div role="radiogroup" aria-label={label} className="inline-flex w-fit flex-wrap rounded-md border border-line-1 bg-bg-2 p-0.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded px-2.5 py-1 text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-neon",
              value === o.value ? "bg-neon/15 text-t-1" : "text-t-3 hover:text-t-1",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
